import type { HeatmapClick, HeatmapPayload } from './heatmap-protocol';

/**
 * Heatmap-målingen på en sidevisning: klik (alle, også uden for links) og det længste scroll samles i hukommelsen og
 * sendes som én pakke med sendBeacon, når siden skjules. Kommer fanen tilbage, sendes ved næste skjul kun de nye
 * klik under samme id, og serveren lægger dem sammen. Grænserne svarer til serverens (HeatmapPayload).
 */

const endpoint = '/api/analytics/heatmap';
const maxClicks = 50;
const maxTargetLength = 300;

export function startHeatmapRecording(): void {
    const id = crypto.randomUUID();
    let pending: HeatmapClick[] = [];
    let recordedClicks = 0;
    let maxScroll = Math.round(window.scrollY);
    let sentScroll = -1;

    document.addEventListener('click', e => {
        // detail 0 er et klik fra tastaturet (Enter på et link): det har ingen position.
        if (!e.isTrusted || e.detail === 0 || recordedClicks >= maxClicks)
            return;

        const click = clickOf(e);
        if (click) {
            pending.push(click);
            recordedClicks++;
        }
    }, true);

    window.addEventListener('scroll', () => {
        maxScroll = Math.max(maxScroll, Math.round(window.scrollY));
    }, { passive: true });

    function send(): void {
        if (pending.length === 0 && maxScroll === sentScroll)
            return;

        const pageHeight = Math.max(document.documentElement.scrollHeight, 1);
        const payload: HeatmapPayload = {
            id,
            path: location.pathname,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            pageHeight,
            maxScroll: Math.min(maxScroll, pageHeight),
            clicks: pending
        };
        navigator.sendBeacon(endpoint, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        pending = [];
        sentScroll = maxScroll;
    }

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
            send();
    });
    window.addEventListener('pagehide', send);
}

/**
 * Klikket relativt til det klikkede element. Et ikon (svg) tæller som sit omgivende element. Bliver DOM-stien for
 * lang, bruges forælderen i stedet.
 */
function clickOf(e: MouseEvent): HeatmapClick | null {
    let element = e.target instanceof Element ? e.target : null;
    if (element instanceof SVGElement)
        element = element.closest('svg')?.parentElement ?? element;

    while (element && element !== document.documentElement) {
        const t = domPath(element);
        if (t.length <= maxTargetLength) {
            const rect = element.getBoundingClientRect();
            return {
                t,
                fx: fraction(e.clientX - rect.left, rect.width),
                fy: fraction(e.clientY - rect.top, rect.height),
                x: Math.max(0, Math.round(e.pageX)),
                y: Math.max(0, Math.round(e.pageY))
            };
        }
        element = element.parentElement;
    }
    return null;
}

/** Elementets sti som CSS-selector: nth-of-type op til nærmeste id, ellers til body. */
function domPath(element: Element): string {
    const parts: string[] = [];
    let node: Element | null = element;
    while (node && node !== document.body && node !== document.documentElement) {
        if (node.id) {
            parts.unshift(`#${CSS.escape(node.id)}`);
            return parts.join(' > ');
        }
        parts.unshift(`${node.tagName.toLowerCase()}:nth-of-type(${indexOfType(node)})`);
        node = node.parentElement;
    }
    parts.unshift('body');
    return parts.join(' > ');
}

function indexOfType(element: Element): number {
    let index = 1;
    for (let sibling = element.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
        if (sibling.tagName === element.tagName)
            index++;
    }
    return index;
}

/** offset / size begrænset til 0–1 med tre decimaler; et element uden størrelse giver midten. */
function fraction(offset: number, size: number): number {
    if (size <= 0)
        return 0.5;
    return Math.round(Math.min(Math.max(offset / size, 0), 1) * 1000) / 1000;
}
